import { useState, useEffect, useCallback } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { setAnalyticsCollectionEnabled, logEvent } from '@/lib/analytics';

const STORAGE_KEY = 'analytics_consent_enabled';

// ─────────────────────────────────────────────────────────────────────────────
// ⚠️⚠️⚠️  PRODUCTION GATE — READ BEFORE SHIPPING  ⚠️⚠️⚠️
//
// DEFAULT_CONSENT_ENABLED is `true` FOR TESTING ONLY, so analytics fires during
// development without having to toggle it on each install.
//
// BEFORE ANY PRODUCTION / STORE RELEASE, this MUST be flipped to `false`.
// The privacy policy (app/(tabs)/settings/privacy.tsx) states Firebase analytics
// is "processed only with your consent (Article 6(1)(a) GDPR), which you can
// withdraw at any time in the app settings." Shipping with the default on means
// collection runs BEFORE consent = a GDPR / policy breach.
//
// TODO(prod): set DEFAULT_CONSENT_ENABLED = false before release.
// ─────────────────────────────────────────────────────────────────────────────
const DEFAULT_CONSENT_ENABLED = true;

export function useAnalyticsConsent() {
  const [enabled, setEnabledState] = useState<boolean>(DEFAULT_CONSENT_ENABLED);

  // Load persisted consent on mount and apply it to the Firebase SDK.
  useEffect(() => {
    (async () => {
      try {
        const stored = await AsyncStorage.getItem(STORAGE_KEY);
        const value = stored === null ? DEFAULT_CONSENT_ENABLED : stored === 'true';
        setEnabledState(value);
        setAnalyticsCollectionEnabled(value).catch(() => {});
        if (stored === null && DEFAULT_CONSENT_ENABLED) {
          // Loud reminder so this isn't missed before release.
          console.warn(
            '[analytics] Consent defaulting to ENABLED — TESTING ONLY. ' +
              'Set DEFAULT_CONSENT_ENABLED=false in hooks/useAnalyticsConsent.ts before production.'
          );
        }
      } catch {
        /* ignore */
      }
    })();
  }, []);

  const setEnabled = useCallback(async (value: boolean) => {
    setEnabledState(value);
    try {
      await AsyncStorage.setItem(STORAGE_KEY, String(value));
    } catch {
      /* ignore */
    }
    // persist first, then log — the server mirror reads consent at send time.
    // Toggling OFF stays silent by design (no collection without consent).
    logEvent('analytics_consent_changed', { enabled: value }).catch(() => {});
    setAnalyticsCollectionEnabled(value).catch(() => {});
  }, []);

  return [enabled, setEnabled] as const;
}